import cloneDeep from 'lodash/cloneDeep';
import api from '@/factories/api';
import EventBus from '@/factories/eventBus';
import isEmpty from 'lodash/isEmpty';
import { getContactName } from '@/filters/helpers';

// Fast paging fires overlapping loadContacts calls; only the last one to settle clears loading.
let pendingContactsLoads = 0;

export default {
  async loadContacts({ commit, getters, dispatch }, data) {
    const {
      filters,
      archived = false,
      fields = [],
      format = 'v2',
      teamFilterOff = false,
    } = data;

    pendingContactsLoads++;
    commit('setContactsLoading', true);

    // clear loaded contacts and filters if contacts type changed
    const storedArchived = getters.contactsInfo?.archived || false;
    if ((archived || storedArchived) && archived != storedArchived) {
      dispatch('clearContactsStore');
      filters.page = 1;
    }

    try {
      const params = { ...filters, archived, fields, format };
      const loadedContacts = await api({ teamFilterOff }).get('contacts', {
        params,
      });
      const currentContacts = getters.contacts;

      let mergedContacts = [];
      if (filters.page > 1) {
        mergedContacts = currentContacts.concat(loadedContacts.results);
      } else {
        mergedContacts = loadedContacts.results;
      }

      commit('setContacts', mergedContacts);
      commit('setContactsInfo', {
        ...getters.contactsInfo,
        filters: filters,
        count: loadedContacts?.count,
        archived,
      });
    } finally {
      pendingContactsLoads--;
      if (!pendingContactsLoads) commit('setContactsLoading', false);
    }
  },

  clearContactsStore({ commit }) {
    commit('setContacts', []);
    commit('setContactsInfo', {});
  },

  async loadSingleContact({ commit }, payload) {
    const { contactId, trackUnavailable } =
      typeof payload === 'object' ? payload : { contactId: payload };

    commit('setContactLoading', true);
    if (trackUnavailable) {
      commit('setUnavailableContactId', null);
    }

    try {
      const contact = await api({ skipErrorModal: trackUnavailable }).get(
        `contacts/${contactId}`
      );
      commit('setActiveContact', contact);
      return contact;
    } catch (error) {
      if (trackUnavailable) {
        if (error?.data?.message === 'Cannot find contact') {
          commit('setUnavailableContactId', contactId);
        } else {
          commit('showInfoModal', {
            type: 'error',
            data: error?.data || { message: 'Unable to load contact' },
            status: error?.status,
          });
        }
      }
      throw error;
    } finally {
      commit('setContactLoading', false);
    }
  },

  async loadContactsRaw(_, params = {}) {
    params.format = 'v2';
    return await api().get('contacts', {
      params,
    });
  },

  async loadSingleContactShort({ commit, getters }, data) {
    const { filters = {}, update = false, format = 'v2', fields = {} } = data;
    try {
      const res = await api().get('contacts', {
        params: { filters, format, fields },
      });

      const contact = Array.isArray(res?.results) ? res.results[0] : null;

      if (data?.filters?.id && contact && contact?.id !== data?.filters?.id) {
        return;
      }

      if (update) {
        commit('updateContactInList', contact);
      }

      return contact;
    } catch (err) {
      throw err;
    }
  },

  async loadContactMetric({ commit, getters }, params) {
    try {
      const metricData = await api().get('contacts/metrics', { params });
      const originalContact = getters.singleContact(params.filters.con_id);

      if (originalContact) {
        const contact = Object.assign(cloneDeep(originalContact), metricData);
        commit('updateContactInList', contact);
      }

      return metricData;
    } catch (err) {
      throw err;
    }
  },

  async createContact({ commit, getters, dispatch }, data) {
    commit('setContactLoading', true);
    try {
      const newContact = await api({ skipErrorModal: true }).post(
        'contacts',
        data
      );
      const fields = getters['fields/all'];
      const convertedContact = convertContact(data, fields, newContact);

      // await contact post-creation processing before adding it to the contacts table
      setTimeout(() => {
        commit('updateContactInList', convertedContact);
      });

      dispatch('portalContacts/updateContactsExpandedTree', convertedContact, {
        root: true,
      });

      dispatch('users/sendAchievement', 'imported_contacts', { root: true });

      return convertedContact;
    } catch (err) {
      throw err;
    } finally {
      commit('setContactLoading', false);
    }
  },

  async updateContact(
    { commit, dispatch, getters },
    { contactId, params, skipRefetch = false }
  ) {
    try {
      await api().put(`contacts/${contactId}`, params);

      const updates = Object.values(params).reduce(
        (acc, fields) => ({ ...acc, ...fields }),
        { id: contactId }
      );

      if (skipRefetch) {
        const existingContact = getters.singleContact(contactId);
        if (existingContact) {
          commit(
            'updateContactInList',
            Object.assign(cloneDeep(existingContact), updates)
          );
        }
      } else {
        dispatch('loadSingleContactShort', {
          filters: { id: contactId },
          update: true,
        });
      }

      EventBus.$emit('contactUpdated', { contactId, params });
      dispatch('portalContacts/updateContactsExpandedTree', updates, {
        root: true,
      });

      return true;
    } catch (err) {
      throw err;
    }
  },

  async mergeContact({ commit, dispatch, getters }, params) {
    const res = await api().post(`contacts/merge`, params);

    const { merge_id, kept_id, dry_run } = res;

    if (dry_run) return res;

    // update kept
    dispatch('loadSingleContactShort', {
      filters: { id: kept_id },
      update: true,
    });

    // delete merged
    let contacts = getters.contacts.filter((i) => i.con_id !== merge_id);
    commit('setContacts', contacts);
  },

  async updateContactField({ getters, dispatch }, { contactId, field, value }) {
    const fieldSchema = getters['fields/getSingleField'](field);
    if (!fieldSchema) return;

    const { bucketId, name } = fieldSchema;

    const params = { [bucketId]: { [name]: value } };

    return await dispatch('updateContact', { contactId, params });
  },

  async deleteContact({ commit, getters }, contactId) {
    try {
      await api().delete(`contacts/${contactId}`);
      let contacts = getters.contacts.filter((i) => i.con_id !== contactId);
      commit('setContacts', contacts);
    } catch (err) {
      throw err;
    }
  },

  async compileMessageForContact({}, { content, contactId = null }) {
    try {
      let path = 'contacts/compile';
      if (contactId) {
        path += `/${contactId}`;
      }
      return await api().post(path, { content });
    } catch (err) {
      throw err;
    }
  },

  async revealContactSecretField({}, { contactId, params }) {
    try {
      return await api().get(`contacts/${contactId}/reveal-secret`, { params });
    } catch (err) {
      throw err;
    }
  },

  async revealPortalSecretField(_, { params }) {
    try {
      return await api().get(`cg/reveal_secret`, { params });
    } catch (err) {
      throw err;
    }
  },

  async toggleBlockContactMessages({ commit }, { contact_id, block }) {
    try {
      const type = block ? 'block' : 'unblock';
      await api().post(`contacts/${type}`, { contact_id });
    } catch (err) {
      throw err;
    }
  },

  async fetchContact({ commit }, contactId) {
    try {
      return await api().get(`contacts/${contactId}`);
    } catch (err) {
      throw err;
    }
  },

  async getContactToken({ commit }, contactId) {
    try {
      const contactData = await api().post(`contacts/${contactId}/token`);
      return contactData ? contactData.token : null;
    } catch (err) {
      throw err;
    }
  },

  async loadFilteredContacts({}, params) {
    params.format = 'v2';

    try {
      return await api().get('contacts', { params });
    } catch (err) {
      throw err;
    }
  },

  async loadContactSuggestions({ commit, getters }, data) {
    commit('setSuggestionsLoading', true);

    let {
      field,
      query,
      created_after = '',
      shareToken,
      teamFilterOff = false,
    } = data;

    const fieldSchema = getters['fields/getSingleField'](field);
    if (fieldSchema?.type === 'list') {
      teamFilterOff = true;
    }

    const params = {
      filters: { [field]: { '?': query } },
      field,
      created_after,
      limit: 25,
      format: 'v2',
    };

    const config = { shareToken, teamFilterOff };

    try {
      let suggestions = await api(config).get('contacts/distinct', { params });
      if (!suggestions) return [];

      if (typeof suggestions[0] === 'object') {
        suggestions = suggestions.map((record) => {
          const { name, text, value, id } = record;

          return {
            text: name || text || value,
            value: id || value,
          };
        });
      }

      return suggestions;
    } finally {
      commit('setSuggestionsLoading', false);
    }
  },

  async loadContactNameSuggestions({ commit }, settings) {
    commit('setSuggestionsLoading', true);

    const { query, filters, created_after = '', shareToken } = settings;

    const params = {
      fields: ['first_name', 'last_name'],
      created_after,
      limit: 25,
      format: 'v2',
    };

    if (query) {
      params.search = query;
    }

    if (filters) {
      params.filters = filters;
    }

    try {
      let data = await api({ shareToken }).get('contacts', { params });
      let results = data?.results || [];

      return results.map((contact) => {
        return {
          id: contact.con_id,
          text: getContactName(contact),
          value: contact.con_id,
        };
      });
    } catch (err) {
      throw err;
    } finally {
      commit('setSuggestionsLoading', false);
    }
  },

  async importContacts({}, url) {
    try {
      return await api().post(`contacts/import`, { url });
    } catch (err) {
      throw err;
    }
  },

  async finishImportContacts(
    { dispatch },
    { id, fields, tags, merge_only, import_label }
  ) {
    try {
      const res = await api().put(`contacts/import/${id}`, {
        fields,
        tags,
        merge_only,
        import_label,
      });
      dispatch('users/sendAchievement', 'imported_contacts', { root: true });
      return res;
    } catch (err) {
      throw err;
    }
  },

  async getContactsImportStatus({ commit }, id) {
    try {
      const importStatus = await api().get(`contacts/import/${id}`);
      commit('setContactsImportStatus', importStatus);
      return importStatus;
    } catch (err) {
      throw err;
    }
  },

  async loadBoards({ commit, getters }, viewId) {
    const id = typeof viewId === 'string' ? viewId : '';
    if (!id && getters.boardsLoaded) return getters.boards;

    try {
      const boards = await api().get(
        'contacts/boards',
        id ? { params: { view_id: id } } : undefined
      );
      const list = Array.isArray(boards) ? boards : [];
      if (!id) commit('setBoards', list);
      return list;
    } catch (err) {
      throw err;
    } finally {
      if (!id) commit('setBoardsLoaded', true);
    }
  },

  async compileTextForContact(_, data) {
    const { contactId, content, inputs = {} } = data;

    try {
      const res = await api().post(`contacts/compile/${contactId}`, {
        content,
        inputs,
      });

      return res?.content || content;
    } catch (err) {
      throw err;
    }
  },

  async unarchiveContact({ dispatch }, contactId) {
    await api().post(`contacts/${contactId}/unarchive`);

    dispatch('checkArchivedContact', {
      contactId,
      archived: false,
    });
  },

  async archiveContact({ dispatch }, contactId) {
    await api().post(`contacts/${contactId}/archive`);

    dispatch('checkArchivedContact', {
      contactId,
      archived: true,
    });
  },

  checkArchivedContact({ commit, getters, dispatch }, data) {
    const { archived, contactId } = data;
    if (isEmpty(getters.contactsInfo)) return;

    if (!!getters.contactsInfo?.archived !== archived) {
      let contacts = getters.contacts.filter((i) => i.con_id !== contactId);
      commit('setContacts', contacts);
      return;
    }

    dispatch('loadSingleContactShort', {
      filters: {
        id: contactId,
        archived: archived ? 'just' : '',
      },
      update: true,
    });
  },

  async inviteContacts({ commit, dispatch }, payload) {
    const res = await api().post(`helpers/send-invite-messages`, payload);
    if (payload.contact_ids) {
      commit('updateInvitedContacts', payload.contact_ids);
    }

    dispatch('users/sendAchievement', 'invited_contacts', { root: true });

    return res;
  },

  async sendContactEvent({}, { contactId, params }) {
    return await api().post(`contacts/${contactId}/event`, params);
  },
};

const convertContact = (contactData, fields, additionalData) => {
  const fieldsObj = fields.find((i) => i.type === 'default')['fields'];
  const convertedObj = Object.keys(contactData).reduce((acc, key) => {
    if (fieldsObj[key]) {
      let keyName = fieldsObj[key].name;
      acc[keyName] = contactData[key];
    }
    return acc;
  }, {});

  return Object.assign(convertedObj, {
    id: contactData.id || additionalData.id,
    con_id: contactData.id || additionalData.id,
    name: contactData.first_name + ' ' + contactData.last_name,
  });
};
